import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import heroPoster from "@/assets/images/hero-poster.jpg";
import { useLang } from "@/lib/useLang";

// 배경 동영상은 용량이 커서 번들에 넣지 않고 public/videos 에서 그대로 제공합니다.
// 큰 화면에는 1080p, 작은 화면(태블릿·휴대폰)에는 720p 를 보내 데이터 사용량을 줄입니다.
// WebM(VP9)이 용량이 작아 먼저 제공하고, 지원하지 않는 브라우저는 MP4(H.264)로 대체합니다.
// 첫 화면(사전 렌더링 HTML)에는 정지 이미지만 두고, 페이지 로딩이 끝난 뒤 화면 크기에 맞는 영상 하나만 받습니다.
// (2026-10-18: 휴대폰에서도 1080p 영상을 먼저 받기 시작해 첫 화면이 늦어지던 문제 수정)
// 데이터 절약 모드 · 느린 회선에서는 영상을 받지 않고 정지 이미지를 유지합니다.
const heroVideoBase = (w: number) => `${import.meta.env.BASE_URL}videos/${w >= 1024 ? "hero-1080" : "hero-720"}`;
const isBrowser = typeof window !== "undefined";
const slowConnection = () => {
  const c = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  return !!c && (!!c.saveData || /(^|-)2g$|^3g$/.test(c.effectiveType || ""));
};

// 제목 첫 줄에서 돌아가며 보여줄 사업 영역 (모두 HTML 에 남아 검색엔진이 읽음)
const ROTATE: Record<"ko" | "en", string[]> = {
  ko: ["선박 IT", "위성통신", "사이버보안", "원격관리"],
  en: ["Vessel IT", "Satellite Comms", "Cyber Security", "Remote Management"],
};
const ROTATE_MS = 2800;

const pad = (n: number) => String(n).padStart(2, "0");

// 글자 단위 등장: 각 글자에 순차 지연을 주어 떠오르게 함.
// 검색엔진이 제목을 "선박 IT" 그대로 읽도록 숨김 속성 없이 일반 공백을 쓰고, 공백 폭은 CSS(white-space: pre)로 유지
function SplitText({ text, from = 0, step = 0.045 }: { text: string; from?: number; step?: number }) {
  return (
    <>
      {Array.from(text).map((ch, i) => (
        <span key={i} className="hero-letter" style={{ "--d": `${from + i * step}s` } as React.CSSProperties}>
          {ch}
        </span>
      ))}
    </>
  );
}

// 검색 로봇에는 로테이션 없이 첫 키워드("선박 IT")를 고정 노출 (사용자가 처음 보는 화면과 동일)
const isBot = () => isBrowser && /bot|crawl|spider|slurp|yeti|daum|bingpreview|lighthouse/i.test(navigator.userAgent);

// 미니멀 배너: 영상 위에 한 문장만 중앙 정렬. 상세 정보는 아래 섹션과 서브페이지가 맡습니다.
// 스크롤을 내리면 영상이 어두워지며 살짝 축소되고(--hero-p), 다음 섹션이 그 위를 덮으며 올라옵니다(sticky).
export default function Hero() {
  const { t } = useTranslation();
  const lang = useLang();
  const sectionRef = useRef<HTMLElement>(null);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [videoBase, setVideoBase] = useState<string | null>(null);
  const [word, setWord] = useState(0);
  // 실시간 시계: 서버 렌더링과 첫 hydrate 는 동일하게 빈 값, 이후 매분 갱신
  const [clock, setClock] = useState<{ kst: string; utc: string } | null>(null);

  // hydrate 가 끝난 뒤 실제 환경(화면 폭 · 모션 감소 설정 · 회선)을 반영하고, 페이지 로딩이 끝나면 영상을 붙임
  useEffect(() => {
    if (!isBrowser) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setReduceMotion(reduce);
    if (reduce || slowConnection()) return;
    let timer = 0;
    const start = () => {
      timer = window.setTimeout(() => setVideoBase(heroVideoBase(window.innerWidth)), 200);
    };
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
    return () => {
      window.removeEventListener("load", start);
      window.clearTimeout(timer);
    };
  }, []);

  // 키워드 로테이션
  useEffect(() => {
    if (reduceMotion || isBot()) return;
    const id = setInterval(() => setWord((w) => (w + 1) % ROTATE[lang].length), ROTATE_MS);
    return () => clearInterval(id);
  }, [lang, reduceMotion]);

  // 부산(KST) · UTC 시계
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const kst = new Date(now.getTime() + (now.getTimezoneOffset() + 540) * 60000);
      setClock({
        kst: `${pad(kst.getHours())}:${pad(kst.getMinutes())}`,
        utc: `${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}`,
      });
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, []);

  // 스크롤 진행도(0~1)를 --hero-p 로 전달: 영상 축소 · 어두워짐, 문구 위로 이동
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || reduceMotion) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const p = Math.min(1, Math.max(0, window.scrollY / Math.max(1, window.innerHeight)));
      el.style.setProperty("--hero-p", p.toFixed(3));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [reduceMotion]);

  // 마우스 위치에 따라 배경이 살짝 따라오는 패럴랙스 (CSS 변수로 전달)
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || reduceMotion) return;
    const onMove = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty("--mx", String((e.clientX - r.left) / r.width - 0.5));
      el.style.setProperty("--my", String((e.clientY - r.top) / r.height - 0.5));
    };
    const onLeave = () => {
      el.style.setProperty("--mx", "0");
      el.style.setProperty("--my", "0");
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [reduceMotion]);

  const reveal = (delay: number) => ({ "--d": `${delay}s` }) as React.CSSProperties;
  const words = ROTATE[lang];

  return (
    <section ref={sectionRef} id="top" className="hero hero-sticky relative w-full overflow-hidden flex flex-col">
      {/* Background video (패럴랙스 + 스크롤 축소 래퍼) */}
      <div className="hero-parallax absolute -inset-6">
        {reduceMotion || !videoBase ? (
          <img src={heroPoster} alt="" fetchPriority="high" className="w-full h-full object-cover object-center" />
        ) : (
          // 밝기 필터는 영상 디테일을 뭉개므로 쓰지 않고, 어두운 막도 글자 가독성에 필요한 만큼만 둡니다.
          <video
            key={videoBase}
            className="w-full h-full object-cover object-center"
            poster={heroPoster}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
          >
            <source src={`${videoBase}.webm`} type="video/webm" />
            <source src={`${videoBase}.mp4`} type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-background-950/30"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/40 via-transparent to-foreground-950"></div>
        {/* 스크롤에 따라 짙어지는 막 */}
        <div className="hero-dim absolute inset-0 bg-foreground-950"></div>
      </div>

      <div className="hero-orb hero-orb-1"></div>
      <div className="hero-orb hero-orb-2"></div>
      <div className="hero-grid absolute inset-0 opacity-10"></div>

      {/* Content: 중앙 정렬 — 라이브 상태 칩 · 두 줄 제목 · 설명 두 줄 */}
      <div className="hero-content relative z-10 flex-1 flex flex-col items-center justify-center text-center px-4 md:px-10 pt-28 pb-10">
        <div className="max-w-4xl flex flex-col items-center">
          {/* 라이브 상태 칩: 헬프데스크 운영 중 · 부산 시각 · UTC */}
          <div
            className="hero-reveal inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-1.5 rounded-full border border-background-50/30 bg-background-950/30 backdrop-blur-sm mb-6 text-background-50 text-[11px] md:text-xs tracking-[0.18em] font-medium font-label"
            style={reveal(0.1)}
          >
            <span className="inline-flex items-center gap-2 whitespace-nowrap">
              <span className="relative w-2 h-2 flex items-center justify-center">
                <span className="absolute w-2 h-2 rounded-full bg-accent-400 animate-ping opacity-75"></span>
                <span className="w-2 h-2 rounded-full bg-accent-400"></span>
              </span>
              {t("hero.live")}
            </span>
            <span className="hidden sm:inline text-background-50/40">|</span>
            <span className="whitespace-nowrap tabular-nums">
              BUSAN <span className="text-accent-200">{clock?.kst ?? "--:--"}</span> KST
            </span>
            <span className="hidden sm:inline text-background-50/40">|</span>
            <span className="whitespace-nowrap tabular-nums">
              UTC <span className="text-accent-200">{clock?.utc ?? "--:--"}</span>
            </span>
          </div>

          <h1 className="font-heading text-background-50 text-4xl md:text-6xl lg:text-7xl font-bold leading-[0.98] mb-6 tracking-tight">
            {/* 1행: 사업 영역 키워드 로테이션. 제목에는 현재 단어 하나만 두어 "선박 IT 통합관리 파트너"로 읽히게 함
                (예전처럼 네 단어를 모두 겹쳐 두면 제목 텍스트가 "선박 IT위성통신사이버보안…"으로 뭉쳐 검색 신호가 약해짐) */}
            <span className="hero-reveal block" style={reveal(0.2)}>
              <span key={word} className={word === 0 ? "inline-block" : "hero-word-in inline-block"}>
                {word === 0 ? <SplitText text={words[0]} from={0.3} /> : words[word]}
              </span>
            </span>{" "}
            <span className="hero-reveal block" style={reveal(0.55)}>
              <span className="text-shimmer">{t("hero.title2")}</span>
            </span>
          </h1>

          <p
            className="hero-reveal text-background-100 text-lg md:text-2xl font-light max-w-3xl leading-relaxed"
            style={reveal(0.8)}
          >
            {t("hero.desc1")}
            <br />
            <span className="text-background-200 text-base md:text-lg">{t("hero.desc2")}</span>
          </p>
        </div>
      </div>

      {/* Scroll indicator */}
      <a
        href="#intro"
        className="hero-reveal relative z-10 mb-10 self-center flex flex-col items-center gap-2 text-background-100 hover:text-accent-300 transition-colors cursor-pointer"
        style={reveal(1.3)}
      >
        <span className="text-[10px] tracking-[0.3em]">SCROLL</span>
        <div className="relative w-px h-10 bg-background-50/20 overflow-hidden">
          <div className="scroll-drop absolute left-0 top-0 w-px h-4 bg-accent-300"></div>
        </div>
      </a>
    </section>
  );
}
